import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import styles from "./aiSettings.module.scss";
import Spinner from "../../components/spinner";
import { useLanguage } from "../../context/languageContext";
import TaskSettings from "./TaskSettings";

const API = process.env.REACT_APP_API_ROOT;

/**
 * AI Settings — one tab per AI task (summary generation, overview generation,
 * translation), each with its own service, model, reasoning effort, token
 * budget, prompts and version history.
 *
 * Everything offered here comes from `GET /ai-config/options`: which services
 * have a key, OpenAI's model list, the efforts, and each task's in-force
 * settings and shipped prompts. The server validates every save against the
 * same catalogue, so the page can't offer something that would be rejected.
 * The open tab lives in the URL (`?task=`) so a link lands on the right one.
 */
const AiSettings = () => {
  const { adminInfo } = useLanguage();
  const [params, setParams] = useSearchParams();
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${adminInfo?.token}` }), [adminInfo?.token]);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/ai-config/options`, { headers });
      if (data?.status) setOptions(data.data);
    } catch (error) {
      console.error("AI settings load error::", error);
      toast.error(error.response?.data?.error || "Could not load the AI settings.");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    document.title = "Penspace - AI Settings";
    load();
  }, [adminInfo?.token, load]);

  if (loading) return <Spinner />;
  if (!options) return null;

  const tasks = options.tasks || [];
  const current = tasks.find((t) => t.key === params.get("task")) || tasks[0];

  return (
    <React.Fragment>
      <div className="heading">
        <div>
          <h4>AI Settings</h4>
          <p className={styles.subtitle}>
            Which service, model and reasoning each AI job runs with, and its prompts. Saving publishes a new
            version and makes it active.
          </p>
        </div>
      </div>

      <div className={styles.tabs} role="tablist">
        {tasks.map((task) => (
          <button
            key={task.key}
            type="button"
            role="tab"
            aria-selected={task.key === current?.key}
            className={task.key === current?.key ? styles.tabActive : styles.tab}
            onClick={() => setParams({ task: task.key }, { replace: true })}
          >
            {task.label}
            <span className={styles.tabModel}>{task.active?.model}</span>
          </button>
        ))}
      </div>

      {current && (
        // Keyed by task so each tab starts with its own form, runs and history.
        <TaskSettings key={current.key} task={current} options={options} headers={headers} onSaved={load} />
      )}
    </React.Fragment>
  );
};

export default AiSettings;
